import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { usePostHog } from 'posthog-js/react';

// A Strava OAuth `code` is single-use. React 18 StrictMode (dev) and any remount
// invoke the effect below twice; the duplicate run would re-read already-cleared
// localStorage (losing the return URL) and re-exchange the spent code, then bounce
// the user to the home fallback a few seconds after the correct redirect. Track the
// code we've handled at module scope so the duplicate run bails out. A genuinely new
// OAuth flow carries a different code (and full-page reloads reset this anyway).
let processedAuthCode = null;

function StravaAuthCallbackPage() {
    const [message, setMessage] = useState('Connecting to Strava...');
    const [isLoading, setIsLoading] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const posthog = usePostHog();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const stravaError = params.get('error');
        const returnedState = params.get('state');
        // Strava returns the actual granted scopes here (comma-separated).
        // This is authoritative; the requestedScope we asked for is NOT.
        const grantedScope = params.get('scope') || '';

        // Process each single-use code exactly once (see processedAuthCode note above).
        // The duplicate StrictMode/remount run returns here, before it can clear the
        // return URL, re-exchange the code, or fire a second redirect.
        if (code && processedAuthCode === code) {
            return;
        }
        if (code) {
            processedAuthCode = code;
        }

        // Retrieve state ONCE and then remove it to prevent reuse.
        const storedState = localStorage.getItem('stravaOAuthState');
        localStorage.removeItem('stravaOAuthState'); // Clean up stored state immediately after retrieval

        // Read optional return URL (e.g. challenge page that initiated the OAuth flow).
        // When it's absent we know nothing special asked for a destination, so brand-new
        // users can be routed into the Zwift fix onboarding instead of the default home.
        const explicitReturnUrl = localStorage.getItem('stravaOAuthReturnUrl');
        const returnUrl = explicitReturnUrl || '/';
        localStorage.removeItem('stravaOAuthReturnUrl');

        // Clean the URL bar (can be done early)
        if (window.history.replaceState) {
            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({ path: cleanUrl }, document.title, cleanUrl);
        }

        if (stravaError) {
            setMessage("We couldn't connect to your Strava account. Please try again.");
            setIsLoading(false);
            console.error("Strava OAuth Error:", stravaError);
            setTimeout(() => navigate(returnUrl), 5000);
            return;
        }

        if (!code) {
            setMessage("Connection was cancelled. Please try connecting to Strava again.");
            setIsLoading(false);
            setTimeout(() => navigate(returnUrl), 5000);
            return;
        }

        // Crucial: Check state *after* retrieving and removing it.
        if (!storedState || returnedState !== storedState) {
            setMessage("Security check failed. Please try connecting to Strava again.");
            setIsLoading(false);
            console.error("Strava OAuth State Mismatch Error. Stored: ", storedState, "Returned: ", returnedState);
            setTimeout(() => navigate(returnUrl), 7000);
            return;
        }

        setMessage("Verifying with Mythic Maps...");

        const processStravaAuth = async () => {
            try {
                const signInOrLinkWithStrava = httpsCallable(functions, 'signInOrLinkWithStrava');
                const result = await signInOrLinkWithStrava({
                    code: code,
                    requestedScope: "activity:read,activity:read_all,activity:write,profile:read_all",
                    grantedScope,
                });

                if (result.data && result.data.success) {
                    if (result.data.customToken) {
                        await signInWithCustomToken(auth, result.data.customToken);
                        
                        // Identify user in PostHog with their details
                        if (auth.currentUser && posthog) {
                            posthog.identify(auth.currentUser.uid, {
                                name: auth.currentUser.displayName,
                                email: auth.currentUser.email,
                                signup_method: 'strava',
                                strava_connected: true
                            });
                        }
                        
                        if (result.data.isRestored) {
                            setMessage("Welcome back! Your account has been restored.");
                        } else {
                            setMessage("Successfully connected! Welcome to Mythic Maps!");
                        }
                    } else {
                        setMessage("Strava account successfully linked!");
                        
                        // For existing users linking Strava, still identify them
                        if (auth.currentUser && posthog) {
                            posthog.identify(auth.currentUser.uid, {
                                name: auth.currentUser.displayName,
                                email: auth.currentUser.email,
                                strava_connected: true
                            });
                        }
                    }
                    setIsSuccess(true);
                    setIsLoading(false);
                    
                    if (window.opener && !window.opener.closed) {
                        window.opener.postMessage("stravaAuthSuccess", window.location.origin);
                    }
                    // New users land on the Zwift fix onboarding (their last indoor ride,
                    // fixed), unless an explicit return URL asked for somewhere specific.
                    // "/fix" rather than "/": logged-in "/" redirects to the feed.
                    const destination = (result.data.isNewUser && !explicitReturnUrl) ? '/fix' : returnUrl;
                    setTimeout(() => navigate(destination), 2000);

                    posthog?.capture('strava_auth_callback_success', {
                        timestamp: new Date().toISOString()
                    });
                    
                    if (result.data.isNewUser) {
                        posthog?.capture('new_user_signup', {
                            signup_method: 'strava',
                            timestamp: new Date().toISOString()
                        });
                    } else if (result.data.isRestored) {
                        posthog?.capture('account_restored', {
                            restoration_method: 'strava',
                            timestamp: new Date().toISOString()
                        });
                    }
                } else {
                    const errorMessage = (result.data && result.data.error) || "Something went wrong. Please try again.";
                    setMessage(errorMessage);
                    setIsLoading(false);
                    console.error("signInOrLinkWithStrava Error:", errorMessage);
                    setTimeout(() => navigate(returnUrl), 5000);

                    posthog?.capture('strava_auth_callback_failed', {
                        error_message: errorMessage
                    });
                }
            } catch (err) {
                setMessage("Connection failed. Please check your internet and try again.");
                setIsLoading(false);
                console.error("Cloud Function Call Error (signInOrLinkWithStrava):", err);
                setTimeout(() => navigate(returnUrl), 5000);

                posthog?.capture('strava_auth_callback_failed', {
                    error_message: err.message
                });
            }
        };

        processStravaAuth();

    }, [location, navigate, posthog]); // location should be sufficient to re-trigger if URL changes

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--color-bg-page, #FFF)',
            fontFamily: 'var(--font-primary, Inter, sans-serif)',
            padding: '20px'
        }}>
            <div style={{
                background: 'var(--color-bg-content-area, #FFF)',
                borderRadius: 'var(--border-radius-lg, 12px)',
                padding: '48px 32px',
                boxShadow: 'var(--box-shadow-card)',
                border: '1px solid var(--border-primary)',
                textAlign: 'center',
                maxWidth: '400px',
                width: '100%'
            }}>
                <h2 style={{
                    color: 'var(--color-text-primary, #101828)',
                    fontSize: '24px',
                    fontWeight: '600',
                    margin: '0 0 24px 0'
                }}>
                    {isSuccess ? 'Connection Successful!' : 'Connecting to Strava'}
                </h2>
                
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    marginBottom: '24px'
                }}>
                    {isLoading ? (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid var(--border-primary)',
                            borderTop: '3px solid var(--color-strava-alt)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                    ) : isSuccess ? (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'var(--color-strava-alt)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px'
                        }}>
                            ✓
                        </div>
                    ) : (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'var(--Gray-300, #D0D5DD)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px'
                        }}>
                            !
                        </div>
                    )}
                </div>

                <p style={{
                    color: 'var(--color-text-secondary, #344054)',
                    fontSize: '16px',
                    lineHeight: '1.5',
                    margin: '0 0 20px 0'
                }}>
                    {message}
                </p>

                {!isLoading && (
                    <p style={{
                        color: 'var(--color-text-subtle, #667085)',
                        fontSize: '14px',
                        margin: '0'
                    }}>
                        Redirecting you back to Mythic Maps...
                    </p>
                )}
            </div>
            
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default StravaAuthCallbackPage; 